'use client';

import type { TutorPageDto } from '@peach/types/tutors/tutor-page.dto.js';
import type { TutorBundle } from '@peach/types/tutors/tutor-profile.dto.js';
import type { CourseWithBundle } from '@peach/types/courses/course-public.dto.js';
import type { ClassWithCourse } from '@peach/types/classes/class-public.dto.js';
import { BundleType } from '@peach/types/bundles/types.js';
import type { SanitizedQuiz } from '@/lib/quiz/sanitize';
import type { StorefrontMode } from './storefront-mode';
import { useEffect, useMemo, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { displayName, firstName } from '@/lib/utils/displayName';
import { Identity } from './Identity';
import { OwnerIdentity } from './OwnerIdentity';
import { Tagline } from './TaglineEditor';
import { useOwnerMode } from '@/lib/stores/owner-store';
import { useFunnel } from '@/lib/stores/funnel.store';
import { INTRO_ALREADY_BOUGHT_COPY, ProductCard } from './ProductCard';
import { useCutSale } from './checkout-store';
import { QuizCard } from './QuizCard';
import { CallCard } from './CallCard';
import { TipCard } from './TipCard';
import { PassCard } from './PassCard';
import { IntroCard } from './IntroCard';
import { useFreshPass } from '@/lib/stores/fresh-pass.store';
import { FooterCard } from './FooterCard';
import { ReferCard } from './ReferCard';
import { OwnerProducts } from './OwnerProducts';
import { transformBundles } from '@/components/products/bundle-config';
import type { ScheduleDto } from '@peach/types/schedule/schedule.dto.js';
import { QuizWaitCard } from './QuizWaitCard';
import { OwnerQuizDoors } from './OwnerQuizDoors';
import {
  PageCardColumn,
  PageCards,
  PageColumn,
  PageFrame,
  PAGE_WIDTH,
} from '@/components/layout/PageFrame';
import { useTimezone } from '@/lib/hooks/useTimezone';
import type { CreatorSubscriptionsDto } from '@peach/types/subscriptions/subscription.dto.js';
import { PlanCard } from './subscriptions/PlanCard';
import { PlansEditor } from './subscriptions/PlansEditor';
import { SubscriptionCard } from './subscriptions/SubscriptionCard';
import { nextCallLine } from './intro-copy';
import { callWhen } from '@/lib/utils/when';
import { useClock } from '@/lib/hooks/useClock';
import { hasEnded } from '@/lib/utils/date';

interface Slot {
  key: string;
  node: ReactNode;
}

export interface StorefrontV2Props {
  tutor: TutorPageDto['tutor'];
  /** The quiz the page shows: the default funnel, or on a funnel page that funnel's. */
  quiz: SanitizedQuiz | null;
  /** The owner's weekly hours, edited in their intro card; null for everyone else. */
  mySchedule?: ScheduleDto | null;
  myClasses?: ClassWithCourse[];
  myCourses?: CourseWithBundle[];
  bundles: TutorBundle[];
  focusBundle?: TutorBundle;
  offerOnly?: boolean;
  isOwner: boolean;
  /** The viewer has a page of their own and this is not it: they may refer its owner. */
  canRefer?: boolean;
  loggedIn: boolean;
  userProfile: { name: string | null; username: string | null } | null;
  initialTimezone?: string | null;
  serverNow: number;
  /** The door. A funnel page (PRINCIPLES.md § 3) and a referral link are the page without
      the tip; on a referral link every sign-in and checkout carries the alias. */
  mode: StorefrontMode;
  /** The creator's plans and the viewer's subscription; a page with plans sells nothing else. */
  subscriptions?: CreatorSubscriptionsDto | null;
}

export function StorefrontV2({
  tutor,
  quiz: liveQuiz,
  mySchedule = null,
  myClasses,
  myCourses,
  bundles,
  focusBundle,
  offerOnly,
  isOwner,
  canRefer = false,
  loggedIn,
  userProfile,
  initialTimezone,
  serverNow,
  mode,
  subscriptions = null,
}: StorefrontV2Props) {
  const plans = subscriptions?.plans ?? [];
  const onSubscriptions = plans.length > 0;
  const mySubscription = isOwner ? null : (subscriptions?.subscription ?? null);
  const funnel = mode.kind === 'funnel';
  const squadAlias = mode.kind === 'mate' ? mode.alias : null;
  const dmReplyId = mode.kind === 'funnel' ? mode.replyId : null;
  const name = displayName(tutor);
  // The name a card says to a visitor; the masthead above carries the full one.
  const short = firstName(tutor);

  /**
   * The visit, and what was on the page to convert it. Every card mounts with
   * the page — nothing here is lazy — so this inventory IS the denominator for
   * each card's conversion: a page with no quiz can't lose a visitor at the
   * quiz. Card-level impression events would only restate this, one per card.
   * The numerator is the `auth_completed` its InlineAuth fires, named by
   * `source`; `is_owner` is what keeps a provider reloading their own page out
   * of the visitor count.
   */
  useEffect(() => {
    posthog.capture('storefront_viewed', {
      tutor_username: tutor.username,
      provider_username: tutor.username,
      creator_username: tutor.username,
      logged_in: loggedIn,
      is_owner: isOwner,
      quiz_count: liveQuiz ? 1 : 0,
      product_count: bundles.length,
      has_intro_video: !!tutor.preview?.playbackId,
      referred: !!squadAlias,
      funnel,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const timezone = useTimezone(initialTimezone ?? null);
  // The page clock, so a call that ends while this page stands open stops being
  // the one the intro names.
  const now = useClock(serverNow);
  const setOwning = useOwnerMode((state) => state.setOwning);
  const setFunnel = useFunnel((state) => state.setFunnel);
  const funnelQuizId = funnel ? (liveQuiz?.id ?? null) : null;
  useEffect(() => {
    if (!funnelQuizId) return;
    setFunnel({ tutorUsername: tutor.username, quizId: funnelQuizId, replyId: dmReplyId });
    return () => setFunnel(null);
  }, [funnelQuizId, dmReplyId, tutor.username, setFunnel]);
  // The bar is mounted once by the root layout and cannot be handed props by
  // this page, so the one thing it needs from here — "the viewer owns the page
  // under me" — is announced through the store, and taken back on the way out.
  useEffect(() => {
    setOwning(isOwner);
    return () => setOwning(false);
  }, [isOwner, setOwning]);
  const sortedBundles = useMemo(() => {
    if (offerOnly && focusBundle) return [focusBundle];
    const rest = [...bundles]
      .filter((b) => b.id !== focusBundle?.id)
      .sort((a, b) => {
        const aTrial = a.type === BundleType.TRIAL;
        const bTrial = b.type === BundleType.TRIAL;
        if (aTrial !== bTrial) return aTrial ? -1 : 1;
        return a.price - b.price;
      });
    return focusBundle ? [focusBundle, ...rest] : rest;
  }, [bundles, focusBundle, offerOnly]);
  // The trial and nothing else. The grid sells it too, as its first card: the
  // intro is calendar-first, the grid card the plain buy (2026-09-26).
  const trialBundle = bundles.find((bundle) => bundle.type === BundleType.TRIAL) ?? null;

  const spendablePasses = useMemo(
    () => (myCourses ?? []).filter((course) => course.available > 0),
    [myCourses]
  );
  // Bought from the grid on this visit: it stands in the card that sold it, not in the intro.
  const freshCourseId = useFreshPass((s) =>
    s.fresh?.tutorUsername === tutor.username ? s.fresh.courseId : null
  );
  const freshPass = spendablePasses.find((course) => course.id === freshCourseId) ?? null;
  const visibleCalls = myClasses ?? [];
  // A pass with this creator, spent or not, is what makes a returning client.
  const hasBought = isOwner || (myCourses?.length ?? 0) > 0;
  // The intro is bought once: a client who has had it reads that on its card
  // in place of the price, and checkout refuses it (`INTRO_ALREADY_BOUGHT`).
  const hadIntro =
    !isOwner && (myCourses ?? []).some((course) => course.bundle.type === BundleType.TRIAL);
  // A sign-in inside the intro's checkout re-renders that card away silently.
  const saleCut = useCutSale((s) => s.tutorUsername === tutor.username);
  // Sold to a NEWCOMER with nothing else to do: a call or an unspent pass owns
  // the ask, and a client who has had the intro reads "Buy a pack" instead (it
  // was sold over a spent pass until 2026-09-25, and one client bought it twice).
  const sellableBundle = offerOnly
    ? null
    : isOwner || (!hasBought && visibleCalls.length === 0 && spendablePasses.length === 0)
      ? trialBundle
      : null;

  const renderQuiz = (quiz: SanitizedQuiz) => (
    <QuizCard
      quiz={quiz}
      isOwner={isOwner}
      tutorUsername={tutor.username}
      firstName={short}
      loggedIn={loggedIn}
      squadAlias={squadAlias}
      dmReplyId={dmReplyId}
    />
  );
  /**
   * The four groups a card can belong to, and the whole layout: a card goes in
   * a list, the lists are concatenated into columns below, and nothing is
   * measured — a page's shape follows from what it holds. Below `deck` the
   * columns stack in this same order, so the lists ARE the reading order.
   */
  const quiz: Slot[] = [];
  const intro: Slot[] = [];
  const calls: Slot[] = [];
  // Kept apart from `calls` even though a pass is a call nobody has picked a
  // time for: the tip stands behind the FIRST call, and a pass must not be able
  // to play that part — thanks for a conversation that has not happened yet.
  const passes: Slot[] = [];
  const tips: Slot[] = [];
  const sell: Slot[] = [];
  const refer: Slot[] = [];
  const add = (into: Slot[], key: string, node: ReactNode) => into.push({ key, node });

  // The page carries one quiz: the default funnel, or the funnel this page is.
  if (liveQuiz) add(quiz, liveQuiz.id, renderQuiz(liveQuiz));
  else if (!offerOnly) add(quiz, 'quiz-wait', <QuizWaitCard tutorName={short} />);

  // The owner's doors under their quiz: post it, edit it here, or put another one on the page.
  if (isOwner && !offerOnly && !funnel && liveQuiz)
    add(quiz, 'quiz-doors', <OwnerQuizDoors quiz={liveQuiz} username={tutor.username} />);

  const renderCall = (cls: ClassWithCourse, embedded = false) => (
    <CallCard
      cls={cls}
      tutorUsername={tutor.username}
      timezone={timezone}
      serverNow={serverNow}
      embedded={embedded}
    />
  );
  const renderPass = (course: CourseWithBundle) => (
    <PassCard
      course={course}
      tutorUsername={tutor.username}
      tutorName={name}
      userProfile={userProfile}
      serverNow={serverNow}
    />
  );

  // The intro names the viewer's plan and holds the card that carries it: the
  // first call still ahead (`visibleCalls` leads with a finished one for a day),
  // or with none the first pass. The owner is never their own client.
  //
  // Asked against the TICKING clock, like the card itself: a call ends under an
  // open page, and against the render-time clock the headline went on naming it
  // — with `callWhen` ticking, "is now" for the rest of the visit, over a card
  // that had already retired itself to "an hour ago".
  const nextCall = isOwner ? undefined : visibleCalls.find((cls) => !hasEnded(cls.end, now));
  const nextSubscriptionCall = mySubscription?.calls[0];
  const subscriberLine = !mySubscription
    ? null
    : nextSubscriptionCall && timezone
      ? nextCallLine(short, callWhen(nextSubscriptionCall.start, now, timezone), timezone)
      : mySubscription.plan.callsPerWeek === 0
        ? `Book your welcome call with ${short}`
        : `Pick a time for your call with ${short}`;
  const otherPasses = spendablePasses.filter((course) => course !== freshPass);
  const firstPass = isOwner || nextCall ? undefined : otherPasses[0];
  add(
    intro,
    'intro',
    <IntroCard
      tutorUsername={tutor.username}
      tutorName={name}
      loggedIn={loggedIn}
      playbackId={tutor.preview?.playbackId}
      picture={tutor.picture}
      bundle={sellableBundle}
      nextCallAt={nextCall?.start ?? null}
      hasPass={!!firstPass || (!nextCall && !!freshPass)}
      timezone={timezone}
      offerOnly={offerOnly}
      isOwner={isOwner}
      mySchedule={mySchedule}
      serverNow={serverNow}
      squadAlias={squadAlias}
      note={saleCut && hadIntro ? INTRO_ALREADY_BOUGHT_COPY : null}
      sellsSubscriptions={onSubscriptions}
      headlineOverride={subscriberLine}
    >
      {mySubscription ? (
        <SubscriptionCard
          subscription={mySubscription}
          plans={plans}
          tutorUsername={tutor.username}
          tutorName={short}
          serverNow={serverNow}
        />
      ) : nextCall ? (
        renderCall(nextCall, true)
      ) : firstPass ? (
        renderPass(firstPass)
      ) : null}
    </IntroCard>
  );
  visibleCalls
    .filter((cls) => mySubscription || cls !== nextCall)
    .forEach((cls) => add(calls, cls.id, renderCall(cls)));
  otherPasses
    .filter((course) => mySubscription || course !== firstPass)
    .forEach((course) => add(passes, course.id, renderPass(course)));

  // No call needed, ahead or behind: a viewer decides for themselves whether
  // there is something to thank for, and every version of that question we
  // asked on their behalf hid the card from someone who meant to press it. The
  // one gate left is the sign-in, because the donation endpoint is authed. A funnel or
  // a referral link is a first meeting, too early to ask for thanks.
  if (loggedIn && mode.kind === 'page') {
    add(tips, 'tip', <TipCard tutorUsername={tutor.username} tutorName={short} />);
  }

  // The owner edits products HERE — the /dashboard/products page died
  // 2026-08-21, and the separate row editor that replaced these cards died with
  // the inline one. The owner's cards ARE the editor now, so their whole grid
  // is one slot: the list, the way back from a removal, and the two ways to add.
  //
  // Not on an offer page: the editor is seeded from `tutor.bundles`, which never
  // carries an ephemeral bundle, so the offer had nowhere to appear and the
  // owner read their own catalogue where the recipient reads a price.
  if (onSubscriptions) {
    // A page on subscriptions sells its plans and nothing else; a subscriber manages theirs above.
    if (isOwner) {
      add(sell, 'plans', <PlansEditor plans={plans} username={tutor.username} />);
    } else if (!mySubscription) {
      plans.forEach((plan) =>
        add(
          sell,
          plan.id,
          <PlanCard
            plan={plan}
            tutorUsername={tutor.username}
            loggedIn={loggedIn}
            isOwner={isOwner}
          />
        )
      );
    }
  } else if (isOwner && !offerOnly) {
    add(
      sell,
      'products',
      <div id="sf-products">
        {/* The WHOLE catalogue, never a view of it: a save replaces every bundle
            the editor holds, so what is seeded here is the write set. Any list
            narrowed for display would delete the rest with one keystroke. */}
        <OwnerProducts
          bundles={transformBundles(bundles)}
          currency={bundles[0]?.currency ?? 'USD'}
          username={tutor.username}
        />
      </div>
    );
  } else {
    // A claimed offer is consumed, so its card is gone by the time the pass lands.
    const freshHome = freshPass && sortedBundles.some((b) => b.id === freshPass.bundle.id);
    if (freshPass && !freshHome) add(sell, freshPass.id, renderPass(freshPass));
    sortedBundles.forEach((bundle, i) =>
      add(
        sell,
        bundle.id,
        <div id={i === 0 ? 'sf-products' : undefined}>
          {freshPass && freshPass.bundle.id === bundle.id ? (
            renderPass(freshPass)
          ) : (
            <ProductCard
              holdsPass
              bundle={bundle}
              tutorUsername={tutor.username}
              loggedIn={loggedIn}
              // Not visitors-only any more: the owner reaches this on their own
              // offer, and `is_owner` keeps them out of the funnel.
              isOwner={isOwner}
              autoOpen={bundle.id === focusBundle?.id}
              squadAlias={squadAlias}
              note={
                hadIntro && bundle.type === BundleType.TRIAL ? INTRO_ALREADY_BOUGHT_COPY : undefined
              }
            />
          )}
        </div>
      )
    );
  }

  // Addressed to a provider, not a client, so it closes the page. A list of its own,
  // not appended to `sell`: that one is also the offer page's second column, and a
  // funnel or a referral link was opened to meet one person, so neither hands out doors.
  if (mode.kind === 'page' && canRefer) {
    add(refer, 'refer', <ReferCard tutorUsername={tutor.username} tutorName={short} />);
  }

  /**
   * **Every column is laid, empty or not.** A dropped column changes how wide
   * the survivors are — `PageCards` sizes by child count — so a page's first
   * teammate used to narrow every card on it.
   *
   * The `id` names the column's *place*, not its contents, so a card arriving
   * or leaving never re-keys the column: a finished call adds a tip card beside
   * the quiz, and a key derived from the cards would unmount the quiz mid-play
   * and throw away the score the sign-in was for.
   *
   * A private offer is the tutor and that offer, and nothing to wander off
   * to — no quiz.
   *
   * Teammates are not on the page (2026-09-25): a referral is the `/mate/{alias}`
   * link the referrer shares, so the tracks are quiz / calls / products.
   */
  // The tip rides behind the FIRST call, which the intro holds now. With no call
  // it goes last: an unbooked pass is already paid for, and an ask above it
  // competes with the one action the client is owed.
  const mine = nextCall ? [...intro, ...tips, ...calls, ...passes] : [...intro, ...passes, ...tips];
  const columns = offerOnly
    ? [
        { id: 'tutor', cards: mine },
        { id: 'sell', cards: sell },
      ]
    : [
        { id: 'quiz', cards: quiz },
        { id: 'tutor', cards: mine },
        { id: 'sell', cards: [...sell, ...refer] },
      ];

  return (
    <PageFrame store>
      <PageColumn className={PAGE_WIDTH}>
        {isOwner && !offerOnly ? (
          <OwnerIdentity
            name={name}
            username={tutor.username}
            tagline={tutor.bio ?? null}
            ambassador={tutor.ambassador}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <Identity name={name} ambassador={tutor.ambassador} />
            <Tagline tagline={tutor.bio} />
          </div>
        )}
      </PageColumn>
      <PageCards>
        {columns.map(({ id, cards }) => (
          <PageCardColumn key={id}>
            {cards.map((slot) => (
              <div key={slot.key}>{slot.node}</div>
            ))}
          </PageCardColumn>
        ))}
      </PageCards>
      {/* The closing column is the footer alone. The record and the recruit
          banner came out 2026-09-08, the Add-to-my-page card 2026-09-09 — a
          storefront is a page a client reads, not a provider's tool. */}
      <PageColumn className={PAGE_WIDTH}>
        <FooterCard />
      </PageColumn>
    </PageFrame>
  );
}
